import type { TranscriptSegment, DeepgramTranscript } from '@/types';

type SegmentCallback = (segment: TranscriptSegment) => void;
type ErrorCallback = (error: string) => void;
type StatusCallback = (status: 'connecting' | 'connected' | 'disconnected') => void;

/**
 * DeepgramSpeechService captures audio from the microphone and streams it
 * to our backend, which proxies it to Deepgram for transcription.
 */
export class DeepgramSpeechService {
  private ws: WebSocket | null = null;
  private mediaRecorder: MediaRecorder | null = null;
  private stream: MediaStream | null = null;
  private isListening = false;
  
  private onSegment: SegmentCallback;
  private onError: ErrorCallback;
  private onStatus: StatusCallback;

  constructor(
    onSegment: SegmentCallback,
    onError: ErrorCallback,
    onStatus: StatusCallback = () => {}
  ) {
    this.onSegment = onSegment;
    this.onError = onError;
    this.onStatus = onStatus;
  }

  /**
   * Build WebSocket URL based on environment
   * In dev: ws://localhost:3001/ws/transcribe
   * In prod: wss://yourdomain.com/ws/transcribe (same origin)
   */
  private getWebSocketUrl(authToken?: string): string {
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const host = window.location.host;
    const baseUrl = `${protocol}//${host}/ws/transcribe`;

    // Append auth token if provided
    if (authToken) {
      return `${baseUrl}?token=${encodeURIComponent(authToken)}`;
    }
    return baseUrl;
  }

  /**
   * Start the speech recognition service
   * @param authToken - Optional Clerk session token for WebSocket authentication
   */
  async start(authToken?: string): Promise<void> {
    if (this.isListening) {
      console.warn('Already listening');
      return;
    }

    try {
      this.onStatus('connecting');

      // Request microphone access
      this.stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          channelCount: 1,
          sampleRate: 16000,
          echoCancellation: true,
          noiseSuppression: true,
        },
      });

      // Connect to backend WebSocket with auth token
      const wsUrl = this.getWebSocketUrl(authToken);
      console.log('Connecting to:', wsUrl.replace(/token=[^&]+/, 'token=***'));
      
      this.ws = new WebSocket(wsUrl);

      this.ws.onopen = () => {
        console.log('✓ Connected to transcription service');
        this.onStatus('connected');
        this.startRecording();
      };

      this.ws.onmessage = (event) => {
        this.handleTranscript(event.data);
      };

      this.ws.onerror = (error) => {
        console.error('WebSocket error:', error);
        this.onError('Connection to transcription service failed');
        this.onStatus('disconnected');
      };

      this.ws.onclose = (event) => {
        console.log('WebSocket closed:', event.code, event.reason);
        this.onStatus('disconnected');
        
        if (this.isListening) {
          // Unexpected close - try to stop gracefully
          this.stop();
        }
      };

      this.isListening = true;
    } catch (error) {
      this.onStatus('disconnected');
      
      if (error instanceof DOMException) {
        if (error.name === 'NotAllowedError') {
          this.onError('Microphone permission denied. Please allow access and try again.');
        } else if (error.name === 'NotFoundError') {
          this.onError('No microphone found. Please connect a microphone and try again.');
        } else {
          this.onError(`Microphone error: ${error.message}`);
        }
      } else {
        this.onError('Failed to start speech recognition');
      }
      
      throw error;
    }
  }

  private startRecording(): void {
    if (!this.stream || !this.ws) return;

    // Find best supported audio format
    const mimeTypes = [
      'audio/webm;codecs=opus',
      'audio/webm',
      'audio/ogg;codecs=opus',
      'audio/mp4',
    ];
    
    const mimeType = mimeTypes.find(type => MediaRecorder.isTypeSupported(type));
    
    if (!mimeType) {
      this.onError('No supported audio format found');
      return;
    }

    console.log('Recording with format:', mimeType);

    this.mediaRecorder = new MediaRecorder(this.stream, { 
      mimeType,
      audioBitsPerSecond: 128000,
    });

    this.mediaRecorder.ondataavailable = (event) => {
      if (event.data.size > 0 && this.ws?.readyState === WebSocket.OPEN) {
        this.ws.send(event.data);
      }
    };

    this.mediaRecorder.onerror = (event) => {
      console.error('MediaRecorder error:', event);
      this.onError('Audio recording error');
    };

    // Send audio chunks every 250ms for low latency
    this.mediaRecorder.start(250);
    console.log('✓ Recording started');
  }

  private handleTranscript(data: string): void {
    try {
      const response: DeepgramTranscript = JSON.parse(data);

      // Handle error from backend
      if (response.error) {
        this.onError(response.error);
        return;
      }

      // Convert to our TranscriptSegment format
      if (response.transcript) {
        this.onSegment({
          text: response.transcript,
          timestamp: Date.now(),
          isFinal: response.is_final,
          speaker: response.speaker,
          start: response.start,
          duration: response.duration,
        });
      }
    } catch (error) {
      console.error('Failed to parse transcript:', error);
    }
  }

  stop(): void {
    console.log('Stopping speech service...');
    this.isListening = false;

    // Stop MediaRecorder
    if (this.mediaRecorder && this.mediaRecorder.state !== 'inactive') {
      this.mediaRecorder.stop();
      this.mediaRecorder = null;
    }

    // Stop audio tracks
    if (this.stream) {
      this.stream.getTracks().forEach(track => track.stop());
      this.stream = null;
    }

    // Close WebSocket
    if (this.ws) {
      if (this.ws.readyState === WebSocket.OPEN) {
        this.ws.close(1000, 'Session ended');
      }
      this.ws = null;
    }

    this.onStatus('disconnected');
  }

  getIsListening(): boolean {
    return this.isListening;
  }

  destroy(): void {
    this.stop();
  }
}

/**
 * Check if the browser supports required APIs
 */
export function checkBrowserSupport(): { supported: boolean; reason?: string } {
  if (!navigator.mediaDevices?.getUserMedia) {
    return { 
      supported: false, 
      reason: 'Your browser does not support microphone access. Please use Chrome, Firefox, or Edge.' 
    };
  }

  if (!window.MediaRecorder) {
    return { 
      supported: false, 
      reason: 'Your browser does not support audio recording. Please use a modern browser.' 
    };
  }

  if (!window.WebSocket) {
    return { 
      supported: false, 
      reason: 'Your browser does not support WebSockets. Please use a modern browser.' 
    };
  }

  return { supported: true };
}
